import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SelectOption } from '../types';

interface SelectProps {
  label?: string;
  id?: string;
  error?: string;
  options: SelectOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

/**
 * Eigen keuzelijst in plaats van een native <select>. Reden: de systeem-dropdown
 * ziet er per browser anders uit en laat zich niet in de huisstijl zetten.
 * Bediening blijft volledig via toetsenbord (pijltjes, Home/End, Enter, Escape)
 * en de knop draagt de juiste ARIA-rollen, zodat schermlezers hem als keuzelijst
 * herkennen.
 */
export const Select: React.FC<SelectProps> = ({
  label,
  id,
  error,
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  required = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const reactId = useId();
  const buttonId = id || `select-${reactId}`;
  const listboxId = `${buttonId}-listbox`;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedIndex = options.findIndex(opt => opt.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

  const close = useCallback((refocus = true) => {
    setOpen(false);
    setActiveIndex(-1);
    if (refocus) buttonRef.current?.focus();
  }, []);

  // Klik buiten de component sluit de lijst.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) close(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open, close]);

  // Houd de actieve optie in beeld tijdens toetsenbordnavigatie.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const node = listRef.current?.children[activeIndex] as HTMLElement | undefined;
    node?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const openList = (startAt: number) => {
    if (disabled) return;
    setOpen(true);
    setActiveIndex(startAt);
  };

  const pick = (index: number) => {
    const option = options[index];
    if (!option) return;
    onChange(option.value);
    close();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    const last = options.length - 1;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) openList(selectedIndex >= 0 ? selectedIndex : 0);
        else setActiveIndex(i => (i >= last ? 0 : i + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) openList(selectedIndex >= 0 ? selectedIndex : last);
        else setActiveIndex(i => (i <= 0 ? last : i - 1));
        break;
      case 'Home':
        if (open) { e.preventDefault(); setActiveIndex(0); }
        break;
      case 'End':
        if (open) { e.preventDefault(); setActiveIndex(last); }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (open && activeIndex >= 0) pick(activeIndex);
        else openList(selectedIndex >= 0 ? selectedIndex : 0);
        break;
      case 'Escape':
        if (open) { e.preventDefault(); close(); }
        break;
      case 'Tab':
        if (open) close(false);
        break;
      default:
        // Typeahead: spring naar de eerste optie die met de ingetypte letter begint.
        if (e.key.length === 1 && /\S/.test(e.key)) {
          const from = (open ? activeIndex : selectedIndex) + 1;
          const needle = e.key.toLowerCase();
          const order = [...options.slice(from), ...options.slice(0, Math.max(from, 0))];
          const hit = order.find(opt => opt.label.toLowerCase().startsWith(needle));
          if (hit) {
            const index = options.indexOf(hit);
            if (open) setActiveIndex(index);
            else openList(index);
          }
        }
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label htmlFor={buttonId} className="block text-sm font-medium text-neutral-700 mb-1">
          {label}
          {required && <span className="text-homeme ml-0.5">*</span>}
        </label>
      )}

      <div className="relative" ref={wrapperRef}>
        <button
          ref={buttonRef}
          type="button"
          id={buttonId}
          disabled={disabled}
          onClick={() => (open ? close() : openList(selectedIndex >= 0 ? selectedIndex : 0))}
          onKeyDown={handleKeyDown}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-invalid={Boolean(error)}
          className={`flex w-full items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2 text-left text-sm transition-colors
            ${error ? 'border-red-500' : open ? 'border-homeme' : 'border-neutral-300 hover:border-neutral-400'}
            ${open ? 'ring-2 ring-homeme/25' : ''}
            ${disabled ? 'cursor-not-allowed bg-neutral-100 text-neutral-400' : 'text-neutral-900'}
            focus:outline-none focus:border-homeme focus:ring-2 focus:ring-homeme/25`}
        >
          <span className={selected ? 'truncate' : 'truncate text-neutral-400'}>
            {selected ? selected.label : placeholder || t('select_placeholder', 'Kies een optie')}
          </span>
          <svg
            className={`h-4 w-4 flex-none text-neutral-400 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="m5 7.5 5 5 5-5" />
          </svg>
        </button>

        {open && (
          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            aria-labelledby={buttonId}
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            className="absolute z-30 mt-1.5 max-h-60 w-full overflow-y-auto rounded-lg border border-neutral-200 bg-white py-1 shadow-lg focus:outline-none"
          >
            {options.map((option, index) => {
              const isSelected = option.value === value;
              const isActive = index === activeIndex;
              return (
                <li
                  key={option.value}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => pick(index)}
                  className={`flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm transition-colors
                    ${isSelected ? 'font-semibold text-homeme-dark' : 'text-neutral-700'}
                    ${isActive ? (isSelected ? 'bg-homeme/15' : 'bg-neutral-100') : isSelected ? 'bg-homeme/10' : ''}`}
                >
                  <span className="truncate">{option.label}</span>
                  {isSelected && (
                    <svg className="h-4 w-4 flex-none text-homeme" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="m4 10.5 4 4 8-9" />
                    </svg>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
};
